import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTarget,
  FiCheckCircle,
  FiArrowRight,
  FiEye,
  FiZap,
  FiSmartphone,
  FiLock,
  FiMail,
  FiUsers,
  FiPlay,
  FiShield,
  FiMessageSquare,
  FiLayout,
  FiSend,
} from 'react-icons/fi';
import ProductPreview from '../components/marketing/ProductPreview';
import { gsap } from 'gsap';
import { ThemeContext } from '../context/ThemeContext';
import MarketingLayout from '../layouts/MarketingLayout';
import Ripple from '../components/Ripple';
import Spotlight from '../components/Spotlight';
import SpotlightCard from '../components/SpotlightCard';

const QuienesSomos = () => {
  const navigate = useNavigate();
  const { modoOscuro } = useContext(ThemeContext);
  const [activeSection, setActiveSection] = useState(0);
  const [isLg, setIsLg] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => setIsLg(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const sectionBg = modoOscuro ? 'bg-black' : 'bg-white';
  const heroText = modoOscuro ? 'text-white' : 'text-gray-900';
  const heroTextMuted = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const iconBox = modoOscuro ? 'bg-[#004AAD]/30 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]';
  const heroBtnPrimary = modoOscuro
    ? 'bg-white text-[#004aad] border-white hover:bg-[#00357a] hover:text-white hover:border-[#00357a]'
    : 'bg-black text-white border-black hover:bg-gray-800';
  const heroBtnSecondary = modoOscuro
    ? 'border-2 border-white text-white hover:bg-white hover:text-[#004aad]'
    : 'border-2 border-black text-black hover:bg-black hover:text-white';

  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);
  const scrollDuration = 1.2;
  const scrollEase = 'power2.inOut';
  const isScrollingRef = useRef(false);
  const activeSectionRef = useRef(0);

  useEffect(() => {
    activeSectionRef.current = 0;
    window.__quienesSomosActiveSection = 0;
    setActiveSection(0);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.fromTo('.hero-badge', { opacity: 0, y: -30 }, { opacity: 1, y: 0, duration: 0.6 });
      tl.fromTo('.hero-title', { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.8 }, '-=0.3');
      tl.fromTo('.hero-desc', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4');
      tl.fromTo('.hero-cta', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6 }, '-=0.3');
      tl.fromTo('.hero-check > *', { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.35, stagger: 0.08 }, '-=0.3');
      tl.fromTo('.hero-showcase', { opacity: 0, y: 40, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 0.8 }, '-=0.5');
    }, section1Ref);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const container = document.getElementById('main-scroll');
    if (!container) return;

    const isMobile = window.innerWidth < 768;
    const sections = [section1Ref, section2Ref, section3Ref];

    const scrollToSection = (index) => {
      isScrollingRef.current = true;

      const targetPosition = index === 0 ? 0 : (() => {
        const targetElement = sections[index].current;
        if (!targetElement) return 0;
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        return container.scrollTop + (targetRect.top - containerRect.top);
      })();

      gsap.to(container, {
        scrollTop: targetPosition,
        duration: scrollDuration,
        ease: scrollEase,
        onComplete: () => {
          isScrollingRef.current = false;
          activeSectionRef.current = index;
          window.__quienesSomosActiveSection = index;
          setActiveSection(index);
        },
      });
    };

    const handleWheel = (e) => {
      if (isMobile || isScrollingRef.current) return;
      e.preventDefault();

      const currentSection = activeSectionRef.current;
      if (e.deltaY > 0 && currentSection < sections.length - 1) {
        scrollToSection(currentSection + 1);
      } else if (e.deltaY < 0 && currentSection > 0) {
        scrollToSection(currentSection - 1);
      }
    };

    const handleScroll = () => {
      if (isScrollingRef.current) return;
      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;

      sections.forEach((ref, index) => {
        if (!ref.current) return;
        if (index === 0) {
          if (scrollTop < containerHeight / 2 && activeSectionRef.current !== 0) {
            activeSectionRef.current = 0;
            window.__quienesSomosActiveSection = 0;
            setActiveSection(0);
          }
          return;
        }
        const rect = ref.current.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const elementTop = rect.top - containerRect.top;
        if (elementTop >= -containerHeight / 2 && elementTop < containerHeight / 2) {
          if (activeSectionRef.current !== index) {
            activeSectionRef.current = index;
            window.__quienesSomosActiveSection = index;
            setActiveSection(index);
          }
        }
      });
    };

    window.__quienesSomosScroll = {
      goToSection: scrollToSection,
      goToHero: () => scrollToSection(0),
    };
    if (!isMobile) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (!isMobile) container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('scroll', handleScroll);
      delete window.__quienesSomosScroll;
    };
  }, []);

  const heroChecks = [
    'Herramienta clara para dueños de negocio, no para contadores expertos',
    'Complementamos a tu estudio; no reemplazamos la facturación oficial',
    '30 días para probar, desde el celu o la compu',
  ];

  const heroJourney = [
    {
      step: '1',
      title: 'Antes',
      hint: 'Chats y planillas',
      icon: FiMessageSquare,
    },
    {
      step: '2',
      title: 'Contsuite',
      hint: 'Todo en un lugar',
      icon: FiLayout,
    },
    {
      step: '3',
      title: 'Cierre',
      hint: 'Mes al contador',
      icon: FiSend,
    },
  ];

  const audiences = [
    {
      icon: <FiZap className="w-5 h-5" />,
      title: 'Dueños de negocio',
      description:
        'Monotributo, emprendimiento o pyme chica: vendés, cobrás y gastás todos los meses y querés orden sin ser “de números”.',
    },
    {
      icon: <FiUsers className="w-5 h-5" />,
      title: 'Contadores y equipos',
      description:
        'Estudios que reciben el mes ordenado, o negocios con varias personas usando la misma herramienta.',
    },
  ];

  const reasons = [
    {
      icon: <FiMail className="w-5 h-5" />,
      title: 'Cobrar sin perseguir',
      description: 'Lista clara de pendientes y recordatorios por WhatsApp cuando hace falta.',
    },
    {
      icon: <FiUsers className="w-5 h-5" />,
      title: 'Cierre con tu contador',
      description: 'Ventas, gastos e impagas en un envío. Tu estudio trabaja con lo mismo que vos.',
    },
    {
      icon: <FiZap className="w-5 h-5" />,
      title: 'Simple de verdad',
      description: 'Empezás en minutos. No es un ERP gigante ni un manual de 200 páginas.',
    },
    {
      icon: <FiLock className="w-5 h-5" />,
      title: 'Hecho en Argentina',
      description: 'Pensado para cómo se trabaja acá: pesos, clientes locales y meses movidos.',
    },
  ];

  const companyFacts = [
    { value: '30', label: 'días para probar' },
    { value: '1', label: 'lugar para tu negocio' },
    { value: '✓', label: 'desde el navegador' },
    { value: '3', label: 'planes' },
  ];

  return (
    <MarketingLayout showNavbar={activeSection === 0}>
      <div className="bg-transparent">
        <section ref={section1Ref} className={`relative min-h-[100svh] lg:min-h-screen flex flex-col justify-start lg:justify-center py-8 sm:py-12 lg:py-0 overflow-x-hidden lg:overflow-hidden ${modoOscuro ? 'bg-black' : 'bg-white'}`}>
          <Ripple
            mainCircleSize={300}
            numCircles={6}
            className={modoOscuro ? 'text-[#1F80FF]/50' : 'text-[#004AAD]/25'}
          />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 lg:pt-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 xl:gap-16 items-center">
              <div className="lg:col-span-6 text-center lg:text-left space-y-4 sm:space-y-5">
                <p className={`hero-badge text-[11px] font-semibold tracking-[0.18em] uppercase ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                  Quiénes somos
                </p>
                <h1 className={`hero-title text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.08] tracking-tight ${heroText}`}>
                  Ordenar el mes no debería ser un segundo trabajo.
                </h1>
                <p className={`hero-desc text-sm sm:text-base lg:text-lg leading-relaxed max-w-lg mx-auto lg:mx-0 ${heroTextMuted}`}>
                  Somos un equipo en Argentina que armó Contsuite para quien vende y cobra todos los meses pero no quiere vivir entre capturas de WhatsApp y planillas eternas.
                </p>
                <ul className="hero-check space-y-2 max-w-md mx-auto lg:mx-0 text-left">
                  {heroChecks.map((item) => (
                    <li key={item} className={`flex items-start gap-2.5 text-sm ${heroTextMuted}`}>
                      <FiCheckCircle className={`w-4 h-4 mt-0.5 shrink-0 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`} />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="hero-cta flex flex-col sm:flex-row gap-2.5 justify-center lg:justify-start pt-1">
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className={`group px-5 py-3 rounded-xl font-semibold text-sm border transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 ${heroBtnPrimary}`}
                  >
                    Empezar gratis
                    <FiArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/funcionalidades')}
                    className={`group px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 active:scale-95 ${heroBtnSecondary}`}
                  >
                    <FiPlay className="inline-block mr-2 w-4 h-4" />
                    Ver cómo funciona
                  </button>
                </div>
              </div>

              <div className="hero-showcase w-full max-w-md mx-auto lg:max-w-none lg:col-span-6">
                <SpotlightCard className={`${box} rounded-3xl border shadow-2xl overflow-hidden`}>
                  <div className={`flex flex-wrap items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b ${modoOscuro ? 'border-gray-700/80' : 'border-gray-200'}`}>
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>
                        Así se ve tu mes
                      </p>
                      <p className={`text-sm font-semibold mt-0.5 ${textPri}`}>Ejemplo con datos ficticios</p>
                    </div>
                    <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${modoOscuro ? 'border-[#1F80FF]/40 text-[#5CA3FF] bg-[#004AAD]/20' : 'border-[#004AAD]/20 text-[#004AAD] bg-[#004AAD]/10'}`}>
                      Hecho en Argentina
                    </span>
                  </div>

                  <div className="max-h-[min(52vh,380px)] overflow-hidden">
                    <ProductPreview
                      variant={isLg ? 'panel' : 'compact'}
                      preset="dashboard"
                      idPrefix="quienes-hero"
                      className="border-0 shadow-none rounded-none"
                    />
                  </div>

                  <div className={`grid grid-cols-3 gap-2 p-3 sm:p-4 border-t ${modoOscuro ? 'border-gray-700/80 bg-gray-950/40' : 'border-gray-200 bg-gray-50/80'}`}>
                    {heroJourney.map((item) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.step}
                          className={`rounded-xl px-2 py-2.5 text-center border ${modoOscuro ? 'border-gray-800 bg-gray-900/80' : 'border-gray-200 bg-white'}`}
                        >
                          <div className={`w-8 h-8 rounded-lg mx-auto mb-1.5 flex items-center justify-center ${iconBox}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <p className={`text-[11px] font-bold leading-tight ${textPri}`}>{item.title}</p>
                          <p className={`text-[10px] mt-0.5 leading-snug ${textMuted}`}>{item.hint}</p>
                        </div>
                      );
                    })}
                  </div>
                </SpotlightCard>
              </div>
            </div>
          </div>
        </section>

        <section ref={section2Ref} className={`relative min-h-screen flex flex-col justify-center py-8 lg:py-10 overflow-hidden ${sectionBg}`}>
          <Spotlight />
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
              <div className="lg:col-span-5">
                <p className={`text-[11px] font-semibold tracking-[0.18em] uppercase mb-3 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                  Por qué existimos
                </p>
                <h2 className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight ${textPri}`}>
                  Lo que nos mueve cada día
                </h2>
                <p className={`text-sm md:text-base leading-relaxed mb-4 ${textSec}`}>
                  Vimos el mismo escenario una y otra vez: mucho trabajo vendiendo, poca claridad al cerrar. Armamos Contsuite para ese momento del mes, no para sumarte otro curso ni otro programa imposible.
                </p>

                <SpotlightCard className={`${box} rounded-2xl p-4 border shadow-sm mb-4`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wide mb-2 ${textMuted}`}>
                    Qué no somos
                  </p>
                  <p className={`text-sm leading-relaxed ${textSec}`}>
                    No reemplazamos a tu contador ni a la facturación oficial. Te ayudamos a llegar al cierre con el mes ordenado y con respuestas simples.
                  </p>
                </SpotlightCard>

                <SpotlightCard className={`${box} rounded-2xl p-5 border shadow-sm space-y-4`}>
                  <div>
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBox}`}>
                        <FiTarget className="w-4 h-4" />
                      </div>
                      <h3 className={`text-base font-bold ${textPri}`}>Misión</h3>
                    </div>
                    <p className={`text-sm leading-relaxed ${textSec}`}>
                      Que sepas qué te deben, qué gastaste y qué le vas a mostrar a tu contador — sin otra planilla paralela.
                    </p>
                  </div>
                  <div className={`h-px ${modoOscuro ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div>
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBox}`}>
                        <FiEye className="w-4 h-4" />
                      </div>
                      <h3 className={`text-base font-bold ${textPri}`}>Visión</h3>
                    </div>
                    <p className={`text-sm leading-relaxed ${textSec}`}>
                      Que cada negocio chico en Argentina pueda cerrar el mes con tranquilidad, aunque no sea “de números”.
                    </p>
                  </div>
                </SpotlightCard>
              </div>

              <div className="lg:col-span-7">
                <p className={`text-[11px] font-semibold tracking-[0.16em] uppercase mb-3 ${textMuted}`}>
                  Para quién es
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  {audiences.map((item) => (
                    <SpotlightCard key={item.title} className={`${box} h-full rounded-xl p-4 border shadow-sm`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${iconBox}`}>
                        {item.icon}
                      </div>
                      <h3 className={`text-sm font-bold mb-1.5 leading-snug ${textPri}`}>{item.title}</h3>
                      <p className={`text-xs leading-relaxed ${textSec}`}>{item.description}</p>
                    </SpotlightCard>
                  ))}
                </div>

                <p className={`text-xs leading-relaxed mb-3 ${textMuted}`}>
                  Si te reconocés en alguno de estos perfiles, Contsuite está pensado para vos.
                </p>
                <SpotlightCard className={`${box} rounded-xl border p-4 shadow-sm`}>
                  <ul className="space-y-2">
                    {['Anotar ventas y gastos en pocos clics', 'Ver si el mes cierra bien', 'Planes que crecen con tu negocio'].map((item) => (
                      <li key={item} className={`flex items-start gap-2 text-sm ${textSec}`}>
                        <FiCheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </SpotlightCard>
                <p className={`text-sm mt-4 ${textSec}`}>
                  ¿Tenés dudas si encaja con tu caso?{' '}
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('openContactModal'))}
                    className={`font-semibold underline underline-offset-2 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}
                  >
                    Escribinos
                  </button>
                </p>

                <SpotlightCard className={`mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl border p-3 shadow-sm ${box}`}>
                  {companyFacts.map((fact) => (
                    <div key={fact.label} className="text-center px-1 py-1.5">
                      <div className="text-lg font-semibold tabular-nums text-[#1F80FF]">{fact.value}</div>
                      <div className={`text-[11px] ${textMuted}`}>{fact.label}</div>
                    </div>
                  ))}
                </SpotlightCard>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.__quienesSomosScroll?.goToHero()}
            className={`hidden lg:flex absolute bottom-8 right-8 w-12 h-12 rounded-full backdrop-blur-sm border items-center justify-center transition-all duration-300 group z-20 ${modoOscuro ? 'bg-white/10 border-white/20 hover:bg-white/20' : 'bg-black/10 border-black/20 hover:bg-black/20'}`}
            aria-label="Volver arriba"
          >
            <FiArrowRight className={`w-5 h-5 rotate-[-90deg] group-hover:-translate-y-1 transition-transform ${modoOscuro ? 'text-white' : 'text-gray-900'}`} />
          </button>
        </section>

        <section ref={section3Ref} className={`relative min-h-screen flex flex-col justify-center py-8 lg:py-10 overflow-hidden ${sectionBg}`}>
          <Spotlight />
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-5 lg:mb-7">
                <p className={`text-[11px] font-semibold tracking-[0.2em] uppercase mb-2 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                  Por qué elegirnos
                </p>
                <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${textPri}`}>
                  Porque el caos del mes tiene solución
                </h2>
                <p className={`text-sm md:text-base max-w-2xl mx-auto leading-relaxed ${textSec}`}>
                  La planilla no te avisa quién te debe. El facturador no arma el resumen para tu estudio. Nosotros juntamos cobros, ventas y gastos para que cierres con datos, no con nervios.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-5 items-stretch">
                <SpotlightCard className={`${box} lg:col-span-5 rounded-2xl p-5 lg:p-6 border shadow-sm flex flex-col`}>
                  <p className={`text-[11px] font-semibold tracking-[0.16em] uppercase mb-1 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                    Cómo lo pensamos
                  </p>
                  <h3 className={`text-lg lg:text-xl font-bold mb-3 ${textPri}`}>
                    Cargás una vez. El mes deja de sentirse caótico.
                  </h3>
                  <p className={`text-sm leading-relaxed mb-4 ${textSec}`}>
                    Cargás lo del día una vez. Después mirás pendientes, mandás un recordatorio o preparás el mes para tu contador — todo desde el mismo lugar.
                  </p>
                  <ul className="space-y-2 flex-1">
                    {[
                      { icon: <FiSmartphone className="w-4 h-4" />, text: 'Desde el celular o la computadora.' },
                      { icon: <FiShield className="w-4 h-4" />, text: 'Empezás simple y sumás cuando lo necesités.' },
                      { icon: <FiCheckCircle className="w-4 h-4" />, text: '30 días para probar con calma.' },
                    ].map((item) => (
                      <li key={item.text} className="flex items-start gap-2.5">
                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconBox}`}>
                          {item.icon}
                        </span>
                        <span className={`text-sm pt-1.5 ${textSec}`}>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-2.5 mt-5">
                    <button
                      type="button"
                      onClick={() => navigate('/register')}
                      className={`group inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${modoOscuro ? 'bg-white text-black hover:bg-gray-200' : 'bg-gray-900 text-white hover:bg-black'}`}
                    >
                      Empezar gratis
                      <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/funcionalidades')}
                      className={`inline-flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${modoOscuro ? 'border-gray-600 text-white hover:bg-gray-800' : 'border-gray-300 text-gray-800 hover:bg-gray-50'}`}
                    >
                      Ver funcionalidades
                    </button>
                  </div>
                </SpotlightCard>

                <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
                  {reasons.map((item) => (
                    <SpotlightCard key={item.title} className={`${box} h-full rounded-xl p-4 lg:p-5 border shadow-sm flex flex-col`}>
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${iconBox}`}>
                        {item.icon}
                      </div>
                      <h3 className={`text-sm lg:text-base font-bold mb-1.5 leading-snug ${textPri}`}>
                        {item.title}
                      </h3>
                      <p className={`text-xs lg:text-sm leading-relaxed ${textSec}`}>
                        {item.description}
                      </p>
                    </SpotlightCard>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.__quienesSomosScroll?.goToHero()}
            className={`hidden lg:flex absolute bottom-8 right-8 w-12 h-12 rounded-full backdrop-blur-sm border items-center justify-center transition-all duration-300 group z-20 ${modoOscuro ? 'bg-white/10 border-white/20 hover:bg-white/20' : 'bg-black/10 border-black/20 hover:bg-black/20'}`}
            aria-label="Volver arriba"
          >
            <FiArrowRight className={`w-5 h-5 rotate-[-90deg] group-hover:-translate-y-1 transition-transform ${modoOscuro ? 'text-white' : 'text-gray-900'}`} />
          </button>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default QuienesSomos;
